export type PbtsDirection = 'up' | 'down';
export type PbtsPolarity = 'MAP' | 'MBP';

export type PbtsTag = {
	label: string;
	direction: PbtsDirection;
	polarity: PbtsPolarity;
};

export type PbtsParseResult =
	| { valid: true; tags: PbtsTag[] }
	| { valid: false; error: string };

const SEGMENT_REGEX = /^(.+?)\s+\*(up|down)\s+#(MAP|MBP)$/i;

export const PBTS_VALIDATION_ERROR =
	'String PBTS inválida. Use o formato: rótulo *up|down #MAP|MBP separado por vírgulas (ex.: desanimo *up #MAP, sono *down #MBP)';

export const isValidPbts = (value: string | null | undefined): boolean =>
	parsePbts(value).valid;

export const parsePbts = (
	value: string | null | undefined,
): PbtsParseResult => {
	if (value == null || value.trim() === '') {
		return { valid: true, tags: [] };
	}

	const segments = value.split(',');
	const tags: PbtsTag[] = [];

	for (const segment of segments) {
		const trimmed = segment.trim();
		if (!trimmed) {
			return { valid: false, error: PBTS_VALIDATION_ERROR };
		}

		const match = trimmed.match(SEGMENT_REGEX);
		if (!match) {
			return { valid: false, error: PBTS_VALIDATION_ERROR };
		}

		const label = match[1].trim();
		if (!label) {
			return { valid: false, error: PBTS_VALIDATION_ERROR };
		}

		tags.push({
			label,
			direction: match[2].toLowerCase() as PbtsDirection,
			polarity: match[3].toUpperCase() as PbtsPolarity,
		});
	}

	return { valid: true, tags };
};

export const formatPbts = (tags: PbtsTag[]): string =>
	tags
		.map((tag) => `${tag.label} *${tag.direction} #${tag.polarity}`)
		.join(', ');

export type PbtsClinicalTrend = 'worsened' | 'improved';

export const resolvePbtsClinicalTrend = (
	tag: Pick<PbtsTag, 'direction' | 'polarity'>,
): PbtsClinicalTrend => {
	if (tag.polarity === 'MAP') {
		return tag.direction === 'up' ? 'worsened' : 'improved';
	}

	return tag.direction === 'up' ? 'improved' : 'worsened';
};

export const resolvePbtsClinicalTrendLabel = (
	tag: Pick<PbtsTag, 'direction' | 'polarity'>,
): string =>
	resolvePbtsClinicalTrend(tag) === 'worsened' ? 'piorou' : 'melhorou';

export const resolvePbtsDirectionLabel = (direction: PbtsDirection): string =>
	direction === 'up' ? 'para cima' : 'para baixo';

export const resolvePbtsDirectionTrendLabel = (
	direction: PbtsDirection,
): string => (direction === 'up' ? 'aumentou' : 'diminuiu');

export const formatPbtsForPrint = (
	value: string | null | undefined,
): string => {
	const parsed = parsePbts(value);
	if (!parsed.valid || parsed.tags.length === 0) return '';

	return parsed.tags
		.map(
			(tag) =>
				`${tag.label.trim()} ${resolvePbtsDirectionTrendLabel(
					tag.direction,
				)}`,
		)
		.join(', ');
};

export const resolvePbtsPolarityLabel = (polarity: PbtsPolarity): string =>
	polarity === 'MAP' ? 'mais alto pior' : 'mais baixo pior';

export const togglePbtsPolarity = (tag: PbtsTag): PbtsTag => ({
	...tag,
	polarity: tag.polarity === 'MAP' ? 'MBP' : 'MAP',
});

export const DEFAULT_PBTS_TAG: PbtsTag = {
	label: '',
	direction: 'up',
	polarity: 'MAP',
};

export const findInvalidPbtsFields = (
	fields: Record<string, string | null | undefined>,
	tagKeys: readonly string[],
): string[] => tagKeys.filter((key) => !isValidPbts(fields[key]));
